import { parse as parseYaml } from "yaml";
import { z } from "zod";

export const layerKinds = [
  "contract",
  "entrypoint",
  "business-logic",
  "authorization",
  "user-interface",
  "infrastructure",
  "data",
  "data-pipeline",
  "integration",
  "wiring",
  "proof",
  "generated",
  "other",
] as const;

export const primaryNatures = [
  "authorization",
  "business-logic",
  "controller-entrypoint",
  "contract",
  "data-persistence",
  "integration",
  "tests-proof",
  "generated",
  "wiring-config",
  "frontend",
  "infrastructure",
  "analytics-data-model",
  "other",
] as const;

export const reviewDomains = ["backend", "frontend", "infrastructure", "data"] as const;
export const scopeBreadths = ["tweak", "focused", "cross-cutting", "system"] as const;
export const contextNovelties = ["familiar", "adjacent", "new"] as const;
export const contextOwners = ["pr", "stack"] as const;
export const changeIntents = ["fix", "enhancement", "refactor", "maintenance", "migration", "dependency", "generated", "other"] as const;
export const stackRoles = ["standalone", "foundation", "middle", "capstone"] as const;
export const riskTags = [
  "authorization",
  "tenancy",
  "security",
  "concurrency",
  "irreversible-effect",
  "external-effect",
  "migration",
  "public-contract",
  "privacy",
  "accessibility",
  "performance",
  "cost",
  "mechanical",
] as const;

export const scrutinyLevels = ["scan", "standard", "deep"] as const;
export const coverageModes = ["direct", "structured-summary", "intentional-skim"] as const;
export const presentations = [
  "shape-sheet",
  "focused-diff",
  "flow",
  "data-change",
  "evidence",
  "generated-summary",
  "visual-change",
  "infrastructure-plan",
  "query-lineage",
] as const;

const idSchema = z.string().regex(/^[a-z0-9][a-z0-9-]*$/);
const safePathSchema = z
  .string()
  .min(1)
  .refine(
    (value) =>
      !value.startsWith("/") &&
      !value.includes("\\") &&
      !value.split("/").some((part) => part === ".." || part === "." || part === ""),
    "must be a safe repository-relative path",
  );

const sourceSchema = z.object({
  baseSha: z.string().regex(/^[a-f0-9]{40}$/i, "must be a full 40-character commit SHA"),
  headSha: z.string().regex(/^[a-f0-9]{40}$/i, "must be a full 40-character commit SHA"),
  descriptionHash: z.string().min(1),
});

const stackPartSchema = z.object({
  pullNumber: z.number().int().positive(),
  title: z.string().min(1),
  position: z.number().int().positive(),
});

const stackContextSchema = z.object({
  revision: idSchema,
  purpose: z.string().min(1),
  approach: z.string().min(1),
  sharedConcepts: z.array(z.string().min(1)).max(6).default([]),
  mainRisk: z.string().min(1),
  parts: z.array(stackPartSchema).min(2),
});

const stackContributionSchema = z.object({
  summary: z.string().min(1),
  newSurface: z.array(z.string().min(1)).min(1).max(6),
});

const scopeSchema = z.object({
  breadth: z.enum(scopeBreadths),
  novelty: z.enum(contextNovelties).default("adjacent"),
  intent: z.enum(changeIntents),
  stack: z.object({
    role: z.enum(stackRoles),
    position: z.number().int().positive(),
    total: z.number().int().positive(),
    id: idSchema.optional(),
    label: z.string().min(1).optional(),
    title: z.string().min(1).optional(),
    contextOwner: z.enum(contextOwners).default("pr"),
    reviewAfter: z.array(z.number().int().positive()).default([]),
    context: stackContextSchema.optional(),
    contribution: stackContributionSchema.optional(),
  }),
});

const queueSchema = z.object({
  estimatedMinutes: z.number().int().positive(),
  difficulty: z.enum(["quick", "normal", "tough"]),
  areas: z.array(z.string().min(1)).min(1),
  priorityReason: z.string().min(1),
});

const reviewRequirementSchema = z.object({
  id: idSchema,
  kind: z.enum(["general", "domain", "security", "specialist"]),
  count: z.number().int().positive(),
  areas: z.array(z.string().min(1)).default([]),
});

const overviewSchema = z.object({
  title: z.string().min(1),
  /**
   * What this PR does, in prose, for a reader who reads nothing else. Deliberately distinct
   * from orientation.definition: the lead is about the change, the definition is reference
   * material about the product area.
   */
  lead: z.string().min(1).optional(),
  orientation: z.object({
    subject: z.string().min(1),
    definition: z.string().min(1),
    purpose: z.string().min(1),
  }).optional(),
  problem: z.string().min(1).optional(),
  approach: z.string().min(1).optional(),
  /**
   * The concrete contents, one entry per real change. An entry may be a plain sentence, or a
   * short scannable label with the sentence behind it — "Add the API surface" reads faster in a
   * list than the clause it stands for, and the reader can drop into the detail only where the
   * label is not enough.
   */
  summary: z.array(z.union([
    z.string().min(1),
    z.object({ label: z.string().min(1), detail: z.string().min(1).optional() }),
  ])).min(1).max(8),
  today: z.string().min(1).optional(),
  after: z.string().min(1).optional(),
  reviewFocus: z.array(z.string().min(1)).max(8).default([]),
  mainRisk: z.string().min(1),
  riskLevel: z.enum(["low", "medium", "high", "critical"]),
  rollout: z.string().min(1).optional(),
  mermaid: z.string().optional(),
});

const reviewPromptSchema = z.object({
  id: idSchema,
  text: z.string().min(1),
  kind: z.enum(["failure-mode", "approach", "tradeoff", "style", "maintainability"]),
  importance: z.enum(["normal", "critical"]),
  rationale: z.string().optional(),
});

const shapeSchema = z.object({
  name: z.string().min(1),
  change: z.enum(["added", "changed", "removed"]),
  fields: z.array(z.string()).default([]),
  usedBy: z.array(z.string()).default([]),
});

const visualSnapshotSchema = z.object({
  src: z.string().regex(/^\/fixtures\//, "visuals must use a local fixture path"),
  alt: z.string().min(1),
  caption: z.string().min(1),
});

const visualChangeSchema = z.object({
  before: visualSnapshotSchema,
  after: visualSnapshotSchema,
});

const infrastructurePlanSchema = z.object({
  create: z.number().int().nonnegative(),
  change: z.number().int().nonnegative(),
  destroy: z.number().int().nonnegative(),
  blastRadius: z.string().min(1),
  rollback: z.string().min(1),
});

const regionMeaningSchema = z.object({
  id: idSchema,
  path: safePathSchema,
  nature: z.enum(primaryNatures),
  concerns: z.array(z.enum(riskTags)).default([]),
  scrutiny: z.enum(scrutinyLevels),
  treatment: z.enum(["guided", "excluded"]).default("guided"),
  exclusionReason: z.string().min(1).optional(),
  generatedFrom: safePathSchema.optional(),
  confidence: z.enum(["high", "medium", "low"]).default("high"),
});

const fileRegionSchema = regionMeaningSchema.extend({
  scope: z.literal("file"),
});

const rangeRegionSchema = regionMeaningSchema.extend({
  scope: z.literal("range"),
  side: z.enum(["LEFT", "RIGHT"]),
  startLine: z.number().int().positive(),
  endLine: z.number().int().positive(),
});

const codeMapSchema = z.object({
  verification: z.enum(["exact", "partial"]),
  regions: z.array(z.discriminatedUnion("scope", [fileRegionSchema, rangeRegionSchema])).min(1),
});

const annotationSchema = z.object({
  id: idSchema,
  path: safePathSchema,
  side: z.enum(["LEFT", "RIGHT"]),
  startLine: z.number().int().positive(),
  endLine: z.number().int().positive(),
  author: z.object({
    type: z.enum(["agent", "human"]),
    name: z.string().min(1),
  }),
  tone: z.enum(["context", "attention", "question"]).default("context"),
  body: z.string().min(1),
});

const guideItemSchema = z.object({
  id: idSchema,
  primary: z.boolean(),
  regionIds: z.array(idSchema).min(1),
  coverageMode: z.enum(coverageModes),
  note: z.string().optional(),
});

const layerSchema = z.object({
  id: idSchema,
  domain: z.enum(reviewDomains),
  kind: z.enum(layerKinds),
  title: z.string().min(1),
  scrutiny: z.enum(scrutinyLevels),
  presentation: z.enum(presentations),
  narrative: z.string().min(1),
  promptIds: z.array(z.string()).default([]),
  riskTags: z.array(z.enum(riskTags)).default([]),
  shapes: z.array(shapeSchema).default([]),
  visualChange: visualChangeSchema.optional(),
  infrastructurePlan: infrastructurePlanSchema.optional(),
  diagram: z.string().min(1).optional(),
  generatedFrom: safePathSchema.optional(),
  items: z.array(guideItemSchema).min(1),
});

const coverageSchema = z.object({
  expectedFiles: z.number().int().nonnegative(),
  files: z.array(
    z.object({
      path: safePathSchema,
      layerId: z.string().optional(),
      excluded: z.boolean().default(false),
      reason: z.string().optional(),
      changedLines: z.number().int().nonnegative().optional(),
    }),
  ),
});

export const guideSchema = z.object({
  schemaVersion: z.literal(2),
  repository: z.string().regex(/^[^/]+\/[^/]+$/),
  pullNumber: z.number().int().positive(),
  mode: z.enum(["live", "replay"]),
  domains: z.array(z.enum(reviewDomains)).min(1),
  scope: scopeSchema,
  source: sourceSchema,
  queue: queueSchema,
  reviewRequirements: z.array(reviewRequirementSchema).min(1),
  overview: overviewSchema,
  reviewPrompts: z.array(reviewPromptSchema).default([]),
  codeMap: codeMapSchema,
  annotations: z.array(annotationSchema).default([]),
  layers: z.array(layerSchema).min(1),
  coverage: coverageSchema,
});

export type ReviewGuide = z.infer<typeof guideSchema>;
export type GuideLayer = ReviewGuide["layers"][number];
export type GuideItem = GuideLayer["items"][number];
export type GuidePrompt = ReviewGuide["reviewPrompts"][number];
export type CodeMapRegion = ReviewGuide["codeMap"]["regions"][number];
export type ReviewAnnotation = ReviewGuide["annotations"][number];
export type PrimaryNature = (typeof primaryNatures)[number];
export type RiskTag = (typeof riskTags)[number];
export type CoverageMode = (typeof coverageModes)[number];
export type ReviewPresentation = "fast-lane" | "compact" | "guided";
export type ReviewBriefing = "none" | "brief" | "standard" | "full";

const fastLaneRiskTags = new Set([
  "authorization",
  "tenancy",
  "security",
  "concurrency",
  "irreversible-effect",
  "external-effect",
  "migration",
  "privacy",
]);

export function reviewPresentation(guide: ReviewGuide, facts?: { changedLoc: number }): ReviewPresentation {
  const hasConsequentialConcern = guide.codeMap.regions.some((region) =>
    region.concerns.some((concern) => fastLaneRiskTags.has(concern)),
  );
  const hasSpecialistRequirement = guide.reviewRequirements.some((requirement) =>
    requirement.kind === "security" || requirement.kind === "specialist",
  );
  const fastLane =
    guide.scope.breadth === "tweak" &&
    guide.overview.riskLevel === "low" &&
    guide.queue.estimatedMinutes <= 5 &&
    guide.coverage.expectedFiles <= 3 &&
    (facts === undefined || facts.changedLoc <= 40) &&
    guide.layers.length <= 2 &&
    guide.layers.every((layer) => layer.scrutiny !== "deep") &&
    guide.codeMap.verification === "exact" &&
    !hasConsequentialConcern &&
    !hasSpecialistRequirement;
  if (fastLane) return "fast-lane";
  if (guide.scope.breadth === "tweak" || guide.scope.breadth === "focused") return "compact";
  return "guided";
}

/**
 * Decide how much context must be read before code. This is intentionally
 * independent from review scrutiny: a high-risk but familiar, focused change
 * can need a deep code review without needing a long preface.
 */
export function reviewBriefing(
  guide: ReviewGuide,
  facts?: { changedLoc: number },
  options?: { stackContextSeen?: boolean },
): ReviewBriefing {
  if (reviewPresentation(guide, facts) === "fast-lane") return "none";

  // Once a reviewer has acquired the stack-level model, later PRs should only
  // explain their incremental responsibility. Scrutiny remains unchanged.
  if (options?.stackContextSeen && guide.scope.stack.contextOwner === "stack") return "brief";

  const breadthWeight = { tweak: 0, focused: 1, "cross-cutting": 3, system: 5 }[guide.scope.breadth];
  const noveltyWeight = { familiar: -1, adjacent: 0, new: 2 }[guide.scope.novelty];
  const stackContextCredit = guide.scope.stack.contextOwner === "stack" ? -1 : 0;
  const changedLoc = facts?.changedLoc;
  const sizeWeight = changedLoc === undefined
    ? 0
    : changedLoc > 4_000
      ? 2
      : changedLoc > 1_000
        ? 1
        : changedLoc <= 80
          ? -1
          : 0;
  const layerWeight = guide.layers.length >= 8 ? 1 : 0;
  const criticalWeight = guide.overview.riskLevel === "critical" && guide.scope.novelty === "new" ? 1 : 0;
  const score = breadthWeight + noveltyWeight + stackContextCredit + sizeWeight + layerWeight + criticalWeight;

  if (score <= 1) return "brief";
  if (score <= 4) return "standard";
  return "full";
}

/**
 * The briefing is always the same three steps — what this PR is, what it changes, and what to
 * watch. Only the third is conditional, so its presence is itself a signal: a PR with nothing
 * sensitive in it simply does not get a warnings page.
 */
export interface SummaryItem {
  label?: string;
  detail?: string;
}

/** Normalises the plain-string and labelled forms of overview.summary into one shape. */
export function summaryItems(guide: ReviewGuide): SummaryItem[] {
  return guide.overview.summary.map((entry) =>
    typeof entry === "string" ? { detail: entry } : { label: entry.label, detail: entry.detail },
  );
}

/** Flat text of a summary entry, for word counting and copy checks. */
export function summaryItemText(item: SummaryItem): string {
  return [item.label, item.detail].filter(Boolean).join(" — ");
}

/**
 * What this PR is, what it changes, how big it really is, and what to watch. Only the last is
 * conditional, so its presence is a signal in itself: a PR with nothing sensitive in it simply
 * does not get a warnings page.
 */
export type BriefingStep = "overview" | "changes" | "profile" | "attention";

const attentionConcerns = new Set<RiskTag>([
  "authorization",
  "tenancy",
  "security",
  "concurrency",
  "irreversible-effect",
  "migration",
  "privacy",
  "public-contract",
]);

/** Regions whose concerns are worth naming before the reviewer opens any code. */
export function attentionRegions(guide: ReviewGuide): CodeMapRegion[] {
  return guide.codeMap.regions.filter((region) =>
    region.treatment !== "excluded" && region.concerns.some((concern) => attentionConcerns.has(concern)),
  );
}

/**
 * Deliberately ignores reviewFocus, which a guided review must always declare and so carries no
 * signal. The step earns its place only when the PR actually contains something to warn about;
 * otherwise its absence tells the reviewer there is nothing sensitive here.
 */
export function hasReviewAttention(guide: ReviewGuide): boolean {
  return (
    guide.overview.riskLevel !== "low" ||
    guide.reviewPrompts.some((prompt) => prompt.importance === "critical") ||
    attentionRegions(guide).length > 0
  );
}

export function briefingSteps(
  guide: ReviewGuide,
  facts?: { changedLoc: number },
  options?: { stackContextSeen?: boolean },
): BriefingStep[] {
  const briefing = reviewBriefing(guide, facts, options);
  if (briefing === "none") return [];
  if (briefing === "brief") return ["overview"];
  const steps: BriefingStep[] = ["overview", "changes", "profile"];
  if (hasReviewAttention(guide)) steps.push("attention");
  return steps;
}

export function briefingPageCount(
  guide: ReviewGuide,
  facts?: { changedLoc: number },
  options?: { stackContextSeen?: boolean },
): number {
  return briefingSteps(guide, facts, options).length;
}

export class GuideValidationError extends Error {
  readonly problems: string[];

  constructor(problems: string[]) {
    super(`Chewfile is invalid:\n${problems.map((problem) => `- ${problem}`).join("\n")}`);
    this.name = "GuideValidationError";
    this.problems = problems;
  }
}

function duplicates(values: string[]): string[] {
  const seen = new Set<string>();
  const repeated = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) repeated.add(value);
    seen.add(value);
  }
  return [...repeated];
}

function hostileRenderable(value: string | undefined): boolean {
  return Boolean(value && /<script|javascript:|\bclick\s+[^\n]+\s+(?:href|call)|onerror\s*=/i.test(value));
}

export function validateGuide(input: unknown): ReviewGuide {
  const parsed = guideSchema.safeParse(input);
  if (!parsed.success) {
    throw new GuideValidationError(
      parsed.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`),
    );
  }

  const guide = parsed.data;
  const problems: string[] = [];
  const presentation = reviewPresentation(guide);
  const layerIds = new Set(guide.layers.map((layer) => layer.id));
  const promptIds = new Set(guide.reviewPrompts.map((prompt) => prompt.id));
  const guideDomains = new Set(guide.domains);
  const regionIds = new Set(guide.codeMap.regions.map((region) => region.id));
  const coveragePaths = new Set(guide.coverage.files.map((entry) => entry.path));
  const cannotScanTags = new Set([
    "authorization",
    "tenancy",
    "security",
    "concurrency",
    "irreversible-effect",
    "external-effect",
    "migration",
    "privacy",
  ]);

  if (guide.scope.stack.position > guide.scope.stack.total) {
    problems.push("scope.stack.position cannot exceed scope.stack.total");
  }
  if (guide.scope.stack.role === "standalone" && (guide.scope.stack.position !== 1 || guide.scope.stack.total !== 1)) {
    problems.push("a standalone PR must use stack position 1 of 1");
  }
  if (guide.scope.stack.role !== "standalone" && guide.scope.stack.total < 2) {
    problems.push("a stacked PR must declare at least two PRs");
  }
  if (guide.scope.stack.role === "standalone" && guide.scope.stack.reviewAfter.length > 0) {
    problems.push("a standalone PR cannot declare scope.stack.reviewAfter");
  }
  if (guide.scope.stack.role !== "standalone" && !guide.scope.stack.id) {
    problems.push("a stacked PR must declare scope.stack.id");
  }
  if (guide.scope.stack.contextOwner === "stack" && (!guide.scope.stack.id || !guide.scope.stack.title)) {
    problems.push("stack-owned context requires scope.stack.id and scope.stack.title");
  }
  if (guide.scope.stack.reviewAfter.includes(guide.pullNumber)) {
    problems.push("scope.stack.reviewAfter cannot include the current pull request");
  }
  if (guide.scope.stack.context) {
    const parts = [...guide.scope.stack.context.parts].sort((left, right) => left.position - right.position);
    if (parts.length !== guide.scope.stack.total) {
      problems.push("scope.stack.context.parts must contain every PR in the stack");
    }
    if (parts.some((part, index) => part.position !== index + 1)) {
      problems.push("scope.stack.context.parts positions must be contiguous from 1");
    }
    const currentPart = parts.find((part) => part.pullNumber === guide.pullNumber);
    if (!currentPart || currentPart.position !== guide.scope.stack.position) {
      problems.push("scope.stack.context.parts must identify this PR at scope.stack.position");
    }
    if (!guide.scope.stack.contribution) {
      problems.push("stack context requires scope.stack.contribution for this PR");
    }
  }

  if (presentation === "guided") {
    if (!guide.overview.orientation) {
      problems.push("overview.orientation is required for a guided review");
    }
    // overview.lead is deliberately NOT hard-required: guides published before it existed must
    // still validate on read. assessChewfileCopy warns instead, so new guides are pushed to it.
    for (const field of ["problem", "approach", "today", "after", "rollout"] as const) {
      if (!guide.overview[field]) problems.push(`overview.${field} is required for a guided review`);
    }
    if (guide.overview.reviewFocus.length === 0) {
      problems.push("overview.reviewFocus is required for a guided review");
    }
  } else if (presentation === "compact" && !guide.overview.problem && !guide.overview.approach) {
    problems.push("a compact review requires overview.problem or overview.approach");
  }

  for (const [label, values] of [
    ["layer", guide.layers.map((layer) => layer.id)],
    ["review prompt", guide.reviewPrompts.map((prompt) => prompt.id)],
    ["review requirement", guide.reviewRequirements.map((requirement) => requirement.id)],
    ["region", guide.codeMap.regions.map((region) => region.id)],
    ["annotation", guide.annotations.map((annotation) => annotation.id)],
    ["item", guide.layers.flatMap((layer) => layer.items.map((item) => item.id))],
  ] as const) {
    for (const id of duplicates(values)) problems.push(`duplicate ${label} id '${id}'`);
  }

  if (hostileRenderable(guide.overview.mermaid)) {
    problems.push("overview.mermaid contains unsafe interactive content");
  }

  for (const annotation of guide.annotations) {
    if (!coveragePaths.has(annotation.path)) problems.push(`${annotation.id}: annotation path is missing from file coverage`);
    if (annotation.endLine < annotation.startLine) problems.push(`${annotation.id}: endLine must not be before startLine`);
  }

  const primaryCounts = new Map<string, number>();
  const regionItemCounts = new Map<string, number>();
  const primaryCoverageModes = new Map<string, string>();
  const referencedRegions = new Set<string>();
  const regionLayerScrutiny = new Map<string, Set<string>>();
  for (const layer of guide.layers) {
    if (!guideDomains.has(layer.domain)) {
      problems.push(`${layer.id}: domain '${layer.domain}' is not declared by the Chewfile`);
    }
    if (layer.scrutiny === "scan" && ["authorization", "business-logic"].includes(layer.kind)) {
      problems.push(`${layer.id}: ${layer.kind} layers cannot use scan scrutiny`);
    }
    const highRiskScanTags = layer.riskTags.filter((tag) => cannotScanTags.has(tag));
    if (layer.scrutiny === "scan" && highRiskScanTags.length > 0) {
      problems.push(`${layer.id}: risk tags ${highRiskScanTags.join(", ")} cannot use scan scrutiny`);
    }
    if (layer.presentation === "visual-change" && !layer.visualChange) {
      problems.push(`${layer.id}: visual-change layers must include visualChange`);
    }
    if (layer.presentation === "infrastructure-plan" && !layer.infrastructurePlan) {
      problems.push(`${layer.id}: infrastructure-plan layers must include infrastructurePlan`);
    }
    if (layer.presentation === "query-lineage" && !layer.diagram) {
      problems.push(`${layer.id}: query-lineage layers must include a diagram`);
    }
    if (hostileRenderable(layer.diagram)) problems.push(`${layer.id}: diagram contains unsafe content`);
    for (const promptId of layer.promptIds) {
      if (!promptIds.has(promptId)) problems.push(`${layer.id}: unknown review prompt '${promptId}'`);
    }
    for (const item of layer.items) {
      for (const id of duplicates(item.regionIds)) problems.push(`${item.id}: duplicate region '${id}'`);
      for (const regionId of item.regionIds) {
        if (!regionIds.has(regionId)) {
          problems.push(`${item.id}: unknown codemap region '${regionId}'`);
          continue;
        }
        referencedRegions.add(regionId);
        regionItemCounts.set(regionId, (regionItemCounts.get(regionId) ?? 0) + 1);
        primaryCounts.set(regionId, (primaryCounts.get(regionId) ?? 0) + Number(item.primary));
        if (item.primary) primaryCoverageModes.set(regionId, item.coverageMode);
        const scrutiny = regionLayerScrutiny.get(regionId) ?? new Set<string>();
        scrutiny.add(layer.scrutiny);
        regionLayerScrutiny.set(regionId, scrutiny);
      }
    }
  }

  for (const region of guide.codeMap.regions) {
    if (!coveragePaths.has(region.path)) problems.push(`${region.id}: path is missing from file coverage`);
    if (region.scope === "range" && region.endLine < region.startLine) {
      problems.push(`${region.id}: endLine must not be before startLine`);
    }
    if (region.treatment === "excluded" && !region.exclusionReason) {
      problems.push(`${region.id}: excluded regions must include a reason`);
    }
    if (region.treatment === "guided" && !referencedRegions.has(region.id)) {
      problems.push(`${region.id}: guided codemap region is orphaned from the tour`);
    }
    if (region.treatment === "excluded" && referencedRegions.has(region.id)) {
      problems.push(`${region.id}: excluded codemap region must not appear in the tour`);
    }
    if (region.treatment === "guided" && (primaryCounts.get(region.id) ?? 0) !== 1) {
      const actual = primaryCounts.get(region.id) ?? 0;
      const referencing = regionItemCounts.get(region.id) ?? 0;
      problems.push(
        `${region.id}: exactly one tour item must set primary: true for each region — ${referencing} item(s) reference it, ${actual} of them primary. 'primary' is per region, not per layer, so several primary items in one layer is fine.`,
      );
    }
    if (region.nature === "generated" && !region.generatedFrom) {
      problems.push(`${region.id}: generated regions must name their generating source`);
    }
    if (region.generatedFrom && !coveragePaths.has(region.generatedFrom)) {
      problems.push(`${region.id}: generating source '${region.generatedFrom}' is not a changed file`);
    }
    const highRiskConcerns = region.concerns.filter((tag) => cannotScanTags.has(tag));
    if (region.scrutiny === "scan" && highRiskConcerns.length > 0) {
      problems.push(`${region.id}: concerns ${highRiskConcerns.join(", ")} cannot use scan scrutiny`);
    }
    if (region.scrutiny === "scan" && ["authorization", "business-logic"].includes(region.nature)) {
      problems.push(`${region.id}: ${region.nature} cannot use scan scrutiny`);
    }
    if (region.scrutiny === "deep" && !regionLayerScrutiny.get(region.id)?.has("deep")) {
      problems.push(
        `${region.id}: a deep region must sit in a layer that is itself scrutiny: deep — either raise the owning layer to deep, or drop this region to standard if it does not really need line-by-line reading.`,
      );
    }
    const directRequired =
      region.scrutiny === "deep" ||
      region.nature === "authorization" ||
      (region.nature !== "tests-proof" &&
        region.nature !== "generated" &&
        region.concerns.some((tag) =>
          [
            "authorization",
            "tenancy",
            "security",
            "concurrency",
            "irreversible-effect",
            "external-effect",
            "privacy",
          ].includes(tag),
        ));
    if (
      region.treatment === "guided" &&
      directRequired &&
      primaryCoverageModes.get(region.id) !== "direct"
    ) {
      const why = region.scrutiny === "deep"
        ? "it is scrutiny: deep"
        : region.nature === "authorization"
          ? "it is authorization code"
          : `it carries ${region.concerns.join(", ")}`;
      problems.push(
        `${region.id}: this region must be shown in full because ${why} — set coverageMode: direct on its primary item (currently '${primaryCoverageModes.get(region.id) ?? "none"}').`,
      );
    }
  }

  const seenCoveragePaths = new Set<string>();
  for (const entry of guide.coverage.files) {
    if (seenCoveragePaths.has(entry.path)) problems.push(`duplicate coverage path '${entry.path}'`);
    seenCoveragePaths.add(entry.path);
    if (entry.excluded && !entry.reason) problems.push(`${entry.path}: exclusions require a reason`);
    if (!entry.excluded && (!entry.layerId || !layerIds.has(entry.layerId))) {
      problems.push(`${entry.path}: coverage must reference a known layer or be excluded`);
    }
  }
  if (guide.coverage.files.length !== guide.coverage.expectedFiles) {
    problems.push(
      `coverage declares ${guide.coverage.expectedFiles} files but lists ${guide.coverage.files.length}`,
    );
  }

  if (problems.length > 0) throw new GuideValidationError(problems);
  return guide;
}

export function parseGuideYaml(yaml: string): ReviewGuide {
  return validateGuide(parseYaml(yaml));
}

export function guideKey(guide: ReviewGuide): string {
  return `${guide.repository}#${guide.pullNumber}@${guide.source.headSha}`;
}

export function regionForItem(guide: ReviewGuide, item: GuideItem): CodeMapRegion {
  const region = guide.codeMap.regions.find((candidate) => candidate.id === item.regionIds[0]);
  if (!region) throw new GuideValidationError([`${item.id}: primary codemap region is missing`]);
  return region;
}
